import React, { useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';

interface LiveEditorProps {
  isEditorMode: boolean;
  contentKey: string;
  initialHtml: string;
  onSave: (key: string, html: string) => void;
  // FIX: Replace `keyof JSX.IntrinsicElements` with `React.ElementType` to avoid JSX namespace errors in certain environments.
  tagName?: React.ElementType;
  className?: string;
  placeholder?: string;
}

const LiveEditor: React.FC<LiveEditorProps> = ({
  isEditorMode,
  contentKey,
  initialHtml,
  onSave,
  tagName = 'p',
  className = '',
  placeholder = 'Enter text...'
}) => {
  const elementRef = useRef<HTMLElement>(null);
  const lastHtml = useRef(initialHtml);

  // Configure DOMPurify to allow iframes (YouTube, etc.)
  const sanitizeConfig = {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "target", "src"]
  };

  // Sanitize initial content to ensure safety on first render
  const safeHtml = DOMPurify.sanitize(initialHtml, sanitizeConfig);

  useEffect(() => {
    if (elementRef.current && safeHtml !== elementRef.current.innerHTML) {
      elementRef.current.innerHTML = safeHtml;
    }
    lastHtml.current = safeHtml;
  }, [safeHtml]);

  const handleBlur = () => {
    if (elementRef.current) {
      const newHtml = elementRef.current.innerHTML;
      if (newHtml !== lastHtml.current) {
        // Sanitize before saving to ensure database stays clean
        const cleanHtml = DOMPurify.sanitize(newHtml, sanitizeConfig);
        onSave(contentKey, cleanHtml);
        lastHtml.current = cleanHtml;
      }
    }
  };

  const Tag = tagName as React.ElementType;

  if (!isEditorMode) {
    return <Tag className={className} dangerouslySetInnerHTML={{ __html: safeHtml }} />;
  }

  return (
    <Tag
      ref={elementRef}
      className={`${className} outline-none focus:ring-2 focus:ring-amber-400 focus:bg-amber-50/50 transition-all rounded-sm p-1 -m-1 cursor-text empty:before:content-[attr(data-placeholder)] empty:before:text-stone-400/70 empty:before:pointer-events-none border-2 border-transparent hover:border-amber-400/50 hover:border-dashed`}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
      data-placeholder={placeholder}
    />
  );
};

export default LiveEditor;